import toFormattedText from '@peloton/copy/toFormattedText';
import { Language } from '@peloton/internationalize/models/language';
import { Locale } from '@peloton/internationalize/models/locale';
import type { Tags, Social, Workouts } from '@members/copy/models.generated';
import de from '@members/copy/static/namespacedCopy/de-DE.production.generated.json';
import en from '@members/copy/static/namespacedCopy/en-US.production.generated.json';
import es from '@members/copy/static/namespacedCopy/es-MX.production.generated.json';

export const languageToCopyMap = {
  [Language.German]: de,
  [Language.English]: en,
  [Language.Spanish]: es,
  [Locale.EnglishUnitedStates]: en,
  [Locale.GermanGermany]: de,
  [Locale.SpanishMexico]: es,
  [Locale.EnglishCanada]: en,
  [Locale.EnglishUnitedKingdom]: en,
  [Locale.EnglishAustralia]: en,
};

export const toCopyString = (collection: string) => (
  language: Language | Locale,
  key: string,
) => languageToCopyMap[language][collection][key] ?? '';

const toTagsCopyForLanguage = (language: Language) => (
  id: typeof Tags[keyof typeof Tags],
  variables: object = {},
) => toFormattedText(toCopyString('tagsCollection')(language, id), variables) as string;

export const toTagDetailsCopyForLanguage = (language: Language, tagName: string) => {
  const toTagsCopy = toTagsCopyForLanguage(language);

  const title = toTagsCopy('shareTitle', { tagName });
  const description = toTagsCopy('shareDescription', { tagName });

  return {
    description,
    title,
  };
};

const toSocialCopyForLanguage = (language: Language) => (
  id: typeof Social[keyof typeof Social],
  variables: object = {},
) => toFormattedText(toCopyString('socialCollection')(language, id), variables) as string;

export const toScheduleEventDetailsCopyForLanguage = (
  language: Language,
  classTitle: string,
  startTime: string,
) => {
  const toSocialCopy = toSocialCopyForLanguage(language);

  const joinScheduledClass = toSocialCopy('joinScheduledClass', {
    classTitle,
    startTime,
  });

  return { joinScheduledClass };
};

export const toInviteDetailsCopyForLanguage = (
  language: Language,
  occasionTitle: string,
  hostname: string,
  time: string,
  type: string,
) => {
  const toSocialCopy = toSocialCopyForLanguage(language);

  const inviteDetailsTitle = toSocialCopy('shareWithOccasion', {
    hostname,
    time,
    type,
    occasionTitle,
  });

  return { inviteDetailsTitle };
};

const toCopyTemplate = toCopyString('workoutsCollection');

const toWorkoutsCopyForLanguage = (language: Language) => (
  id: typeof Workouts[keyof typeof Workouts],
  variables: object = {},
) => toFormattedText(toCopyTemplate(language, id), variables) as string;

export const toGenericWorkoutDetailsCopyForLanguage = (language: Language) => {
  const toWorkoutsCopy = toWorkoutsCopyForLanguage(language);

  const title = toWorkoutsCopy('workedOutWithPeloton');
  const description = toWorkoutsCopy('genericShareDescription');
  return {
    description,
    title,
  };
};
